/**
 * FikoForgingWeldingCost - Dovme, Kaynak, Isil Islem ve Yuzey Isleme Maliyet Modulu
 *
 * Acik/kapali kalip dovme, MIG/TIG/Nokta/Lazer kaynak,
 * isil islem ve yuzey kaplama surecleri icin maliyet hesaplama.
 */

import { FikoMaterialDB, MaterialProperties, ManufacturingProcessType } from './FikoMaterialDB';
import { CostLineItem } from './FikoCostEngine';

// ============================================================
// FORGING TYPES
// ============================================================

export type ForgingProcessType = 'open_die_forging' | 'closed_die_forging';

export interface ForgingCostInput {
    partGeometry: {
        volumeCm3: number;
        surfaceAreaCm2: number;
        boundingBox: { x: number; y: number; z: number };
        complexityScore: number;
    };
    material: MaterialProperties;
    batchSize: number;
    annualVolume: number;
    forgingProcess: ForgingProcessType;
    requiresHeatTreatment?: boolean;
    machiningAllowanceMM?: number;
}

export interface ForgingCostResult {
    costPerPart: number;
    costPerKg: number;
    breakdown: CostLineItem[];
    notes: string[];
    notesTR: string[];
}

// ============================================================
// WELDING TYPES
// ============================================================

export type WeldingProcessType = 'welding_mig' | 'welding_tig' | 'welding_spot' | 'welding_laser';

export interface WeldingCostInput {
    weldLength: number;            // mm
    weldType: 'fillet' | 'butt' | 'lap' | 'spot';
    materialThickness: number;     // mm
    material: MaterialProperties;
    weldingProcess: WeldingProcessType;
    spotWeldCount?: number;        // Nokta kaynak icin
    batchSize: number;
}

export interface WeldingCostResult {
    costPerPart: number;
    breakdown: CostLineItem[];
    weldTimeMinutes: number;
    notes: string[];
    notesTR: string[];
}

// ============================================================
// HEAT TREATMENT TYPES
// ============================================================

export type HeatTreatmentType = 'annealing' | 'normalizing' | 'quench_temper' | 'case_hardening' | 'stress_relief' | 'aging';

export interface HeatTreatmentCostInput {
    partWeightKg: number;
    material: MaterialProperties;
    treatmentType: HeatTreatmentType;
    batchSize: number;
}

export interface HeatTreatmentCostResult {
    costPerPart: number;
    breakdown: CostLineItem[];
    notes: string[];
    notesTR: string[];
}

// ============================================================
// SURFACE TREATMENT TYPES
// ============================================================

export type SurfaceTreatmentType = 'anodizing' | 'plating' | 'painting' | 'powder_coating';

export interface SurfaceTreatmentCostInput {
    surfaceAreaCm2: number;
    material: MaterialProperties;
    treatmentType: SurfaceTreatmentType;
    batchSize: number;
    coatingThickness?: number;     // micron
}

export interface SurfaceTreatmentCostResult {
    costPerPart: number;
    breakdown: CostLineItem[];
    notes: string[];
    notesTR: string[];
}

// ============================================================
// CALCULATOR
// ============================================================

export class FikoForgingWeldingCost {
    private materialDB: FikoMaterialDB;

    constructor(materialDB: FikoMaterialDB) {
        this.materialDB = materialDB;
    }

    // ================================================================
    // FORGING
    // ================================================================

    calculateForging(input: ForgingCostInput): ForgingCostResult {
        const { material, partGeometry, batchSize, annualVolume, forgingProcess } = input;
        const notes: string[] = [];
        const notesTR: string[] = [];
        const breakdown: CostLineItem[] = [];

        const partWeightKg = partGeometry.volumeCm3 * material.density;

        // 1. Billet malzeme (forging has ~15-25% flash/waste)
        const flashPercent = forgingProcess === 'closed_die_forging' ? 15 : 25;
        const machiningAllowance = input.machiningAllowanceMM || 3;
        const billetWeightKg = partWeightKg * (1 + flashPercent / 100) * (1 + machiningAllowance * 0.02);
        const materialPricePerKg = material.barStockPricePerKg || material.pricePerKg;
        const materialCost = billetWeightKg * materialPricePerKg;

        breakdown.push({
            category: 'material',
            description: `${material.name} billet (${billetWeightKg.toFixed(2)}kg incl. ${flashPercent}% flash)`,
            descriptionTR: `${material.nameTR} kutuk (${billetWeightKg.toFixed(2)}kg, %${flashPercent} capak dahil)`,
            quantity: billetWeightKg,
            unitCost: materialPricePerKg,
            totalCost: materialCost,
            unit: 'kg'
        });

        // 2. Isitma maliyeti (forging temperature)
        const heatingCostPerKg = material.meltingPoint > 1400 ? 0.8 : material.meltingPoint > 800 ? 0.5 : 0.3;
        const heatingCost = billetWeightKg * heatingCostPerKg;
        breakdown.push({
            category: 'machine',
            description: `Heating to forging temperature ($${heatingCostPerKg.toFixed(2)}/kg, ${material.meltingPoint}C x ${heatingCostPerKg}hr)`,
            descriptionTR: `Dovme sicakligina isitma ($${heatingCostPerKg.toFixed(2)}/kg, ${material.meltingPoint}C x ${heatingCostPerKg}saat)`,
            quantity: billetWeightKg,
            unitCost: heatingCostPerKg,
            totalCost: heatingCost,
            unit: 'kg'
        });

        // 3. Kalip maliyeti (kapali kalip icin yuksek)
        if (forgingProcess === 'closed_die_forging') {
            const dieCost = this.estimateForgingDieCost(partGeometry, material);
            const dieLife = 15000; // Tipik celik kalip omru
            const amortizedDie = dieCost / Math.min(annualVolume * 2, dieLife);
            breakdown.push({
                category: 'tooling',
                description: `Forging die ($${dieCost.toFixed(0)} / ${Math.min(annualVolume * 2, dieLife)} parts)`,
                descriptionTR: `Dovme kalıbi ($${dieCost.toFixed(0)} / ${Math.min(annualVolume * 2, dieLife)} parca)`,
                quantity: 1,
                unitCost: amortizedDie,
                totalCost: amortizedDie,
                unit: 'adet'
            });
        }

        // 4. Pres maliyeti
        const pressTonnage = this.estimatePressTonnage(partGeometry, material);
        const pressRate = this.getPressRate(pressTonnage);
        const cycleTimeMin = forgingProcess === 'closed_die_forging' ? 0.5 : 3.0;
        const pressCost = (cycleTimeMin / 60) * pressRate;
        breakdown.push({
            category: 'machine',
            description: `${pressTonnage.toFixed(0)}T press (${cycleTimeMin.toFixed(1)} min, $${pressRate}/hr)`,
            descriptionTR: `${pressTonnage.toFixed(0)}T pres (${cycleTimeMin.toFixed(1)} dk, $${pressRate}/saat)`,
            quantity: cycleTimeMin / 60,
            unitCost: pressRate,
            totalCost: pressCost,
            unit: 'saat'
        });

        // 5. Flash trimming / capak kesme
        const trimCost = partWeightKg * 0.5;
        breakdown.push({
            category: 'post_processing',
            description: 'Flash trimming',
            descriptionTR: 'Capak kesme',
            quantity: 1,
            unitCost: trimCost,
            totalCost: trimCost,
            unit: 'adet'
        });

        // 6. Iscilik
        const laborMinutes = forgingProcess === 'closed_die_forging' ? 3 : 15;
        const laborRate = 28;
        const laborCost = (laborMinutes / 60) * laborRate;
        breakdown.push({
            category: 'labor',
            description: `Forging labor (${laborMinutes} min)`,
            descriptionTR: `Dovme isciligi (${laborMinutes} dk)`,
            quantity: laborMinutes / 60,
            unitCost: laborRate,
            totalCost: laborCost,
            unit: 'saat'
        });

        // 7. Setup
        const machineParams = this.materialDB.getMachineParams(forgingProcess as ManufacturingProcessType);
        const setupCost = machineParams ? (machineParams.setupTimeMinutes / 60) * machineParams.hourlyRate / batchSize : 0;
        if (setupCost > 0.01) {
            breakdown.push({
                category: 'setup',
                description: `Setup / ${batchSize} parts`,
                descriptionTR: `Hazirlik / ${batchSize} parca`,
                quantity: 1,
                unitCost: setupCost,
                totalCost: setupCost,
                unit: 'adet'
            });
        }

        const costPerPart = breakdown.reduce((sum, item) => sum + item.totalCost, 0);
        const costPerKg = partWeightKg > 0 ? costPerPart / partWeightKg : 0;

        notes.push(`Billet: ${billetWeightKg.toFixed(2)}kg, Net: ${partWeightKg.toFixed(2)}kg`);
        notes.push(`Press: ${pressTonnage.toFixed(0)}T`);
        notesTR.push(`Kutuk: ${billetWeightKg.toFixed(2)}kg, Net: ${partWeightKg.toFixed(2)}kg`);
        notesTR.push(`Pres: ${pressTonnage.toFixed(0)}T`);

        return { costPerPart: Math.round(costPerPart * 100) / 100, costPerKg: Math.round(costPerKg * 100) / 100, breakdown, notes, notesTR };
    }

    // ================================================================
    // WELDING
    // ================================================================

    calculateWelding(input: WeldingCostInput): WeldingCostResult {
        const { material, weldingProcess, batchSize } = input;
        const notes: string[] = [];
        const notesTR: string[] = [];
        const breakdown: CostLineItem[] = [];

        // 1. Kaynak teli/gaz maliyeti
        const wireConsumptionKgPerMeter = this.getWireConsumption(input.weldType, input.materialThickness);
        const wirePricePerKg = material.wirePricePerKg || material.pricePerKg * 2;
        const totalWireKg = (input.weldLength / 1000) * wireConsumptionKgPerMeter;
        const wireCost = totalWireKg * wirePricePerKg;

        breakdown.push({
            category: 'material',
            description: `Welding wire/rod (${totalWireKg.toFixed(3)}kg for ${input.weldLength}mm)`,
            descriptionTR: `Kaynak teli (${totalWireKg.toFixed(3)}kg, ${input.weldLength}mm icin)`,
            quantity: totalWireKg,
            unitCost: wirePricePerKg,
            totalCost: wireCost,
            unit: 'kg'
        });

        // Koruyucu gaz
        if (weldingProcess === 'welding_mig' || weldingProcess === 'welding_tig') {
            const gasFlowLPerMin = weldingProcess === 'welding_tig' ? 10 : 15;
            const weldSpeed = this.getWeldSpeed(weldingProcess, input.materialThickness);
            const weldTimeMin = input.weldLength / weldSpeed;
            const gasUsedLiters = gasFlowLPerMin * weldTimeMin;
            const gasCostPerLiter = 0.01; // Argon ~$10/1000L
            const gasCost = gasUsedLiters * gasCostPerLiter;
            breakdown.push({
                category: 'material',
                description: `Shielding gas (${gasUsedLiters.toFixed(0)}L)`,
                descriptionTR: `Koruyucu gaz (${gasUsedLiters.toFixed(0)}L)`,
                quantity: gasUsedLiters,
                unitCost: gasCostPerLiter,
                totalCost: gasCost,
                unit: 'litre'
            });
        }

        // 2. Kaynak suresi ve makine maliyeti
        const weldSpeed = this.getWeldSpeed(weldingProcess, input.materialThickness); // mm/min
        let weldTimeMinutes = 0;

        if (weldingProcess === 'welding_spot' && input.spotWeldCount) {
            weldTimeMinutes = input.spotWeldCount * 0.1; // 6 seconds per spot
        } else {
            weldTimeMinutes = input.weldLength / weldSpeed;
        }

        const machineParams = this.materialDB.getMachineParams(weldingProcess as ManufacturingProcessType);
        const hourlyRate = machineParams?.hourlyRate || 50;
        const machineCost = (weldTimeMinutes / 60) * hourlyRate;

        breakdown.push({
            category: 'machine',
            description: `Welding time (${weldTimeMinutes.toFixed(1)} min, $${hourlyRate}/hr)`,
            descriptionTR: `Kaynak suresi (${weldTimeMinutes.toFixed(1)} dk, $${hourlyRate}/saat)`,
            quantity: weldTimeMinutes / 60,
            unitCost: hourlyRate,
            totalCost: machineCost,
            unit: 'saat'
        });

        // 3. Iscilik (kaynak operatoru)
        const laborRate = weldingProcess === 'welding_tig' ? 35 : 28; // TIG requires more skill
        const laborCost = (weldTimeMinutes * 1.5 / 60) * laborRate; // 1.5x for prep + weld
        breakdown.push({
            category: 'labor',
            description: `Welder labor (${(weldTimeMinutes * 1.5).toFixed(0)} min incl. prep)`,
            descriptionTR: `Kaynakci isciligi (${(weldTimeMinutes * 1.5).toFixed(0)} dk, hazirlik dahil)`,
            quantity: weldTimeMinutes * 1.5 / 60,
            unitCost: laborRate,
            totalCost: laborCost,
            unit: 'saat'
        });

        // 4. Son islem (kaynak temizleme, taslama)
        const cleaningCost = (input.weldLength / 1000) * 3; // $3/m
        breakdown.push({
            category: 'post_processing',
            description: 'Weld cleaning / grinding',
            descriptionTR: 'Kaynak temizleme / taslama',
            quantity: input.weldLength / 1000,
            unitCost: 3,
            totalCost: cleaningCost,
            unit: 'metre'
        });

        // 5. Setup
        const setupCost = machineParams ? (machineParams.setupTimeMinutes / 60) * hourlyRate / batchSize : 0;
        if (setupCost > 0.01) {
            breakdown.push({
                category: 'setup',
                description: `Setup / ${batchSize} parts`,
                descriptionTR: `Hazirlik / ${batchSize} parca`,
                quantity: 1,
                unitCost: setupCost,
                totalCost: setupCost,
                unit: 'adet'
            });
        }

        const costPerPart = breakdown.reduce((sum, item) => sum + item.totalCost, 0);

        notes.push(`Weld speed: ${weldSpeed.toFixed(0)} mm/min, Time: ${weldTimeMinutes.toFixed(1)} min`);
        notesTR.push(`Kaynak hizi: ${weldSpeed.toFixed(0)} mm/dk, Sure: ${weldTimeMinutes.toFixed(1)} dk`);

        return { costPerPart: Math.round(costPerPart * 100) / 100, breakdown, weldTimeMinutes, notes, notesTR };
    }

    // ================================================================
    // HEAT TREATMENT
    // ================================================================

    calculateHeatTreatment(input: HeatTreatmentCostInput): HeatTreatmentCostResult {
        const { partWeightKg, material, treatmentType, batchSize } = input;
        const notes: string[] = [];
        const notesTR: string[] = [];
        const breakdown: CostLineItem[] = [];

        const params = this.getHeatTreatmentParams(treatmentType, material);

        // 1. Enerji maliyeti
        const energyCostPerKg = params.energyCostPerKg;
        const energyCost = partWeightKg * energyCostPerKg;
        breakdown.push({
            category: 'machine',
            description: `${params.name} energy ($${energyCostPerKg.toFixed(2)}/kg, ${params.temperatureC}C x ${params.holdTimeHours}hr)`,
            descriptionTR: `${params.nameTR} enerji ($${energyCostPerKg.toFixed(2)}/kg, ${params.temperatureC}C x ${params.holdTimeHours}saat)`,
            quantity: partWeightKg,
            unitCost: energyCostPerKg,
            totalCost: energyCost,
            unit: 'kg'
        });

        // 2. Iscilik
        const laborMinutes = 5 + (treatmentType === 'case_hardening' ? 15 : 5);
        const laborCost = (laborMinutes / 60) * 22;
        breakdown.push({
            category: 'labor',
            description: `Heat treatment labor (${laborMinutes} min)`,
            descriptionTR: `Isil islem isciligi (${laborMinutes} dk)`,
            quantity: laborMinutes / 60,
            unitCost: 22,
            totalCost: laborCost,
            unit: 'saat'
        });

        // 3. Sogutma ortami (yag, su, vb.)
        if (treatmentType === 'quench_temper' || treatmentType === 'case_hardening') {
            const quenchCost = partWeightKg * 0.3;
            breakdown.push({
                category: 'material',
                description: 'Quenching medium',
                descriptionTR: 'Sogutma ortami (yag/su)',
                quantity: 1,
                unitCost: quenchCost,
                totalCost: quenchCost,
                unit: 'adet'
            });
        }

        const costPerPart = breakdown.reduce((sum, item) => sum + item.totalCost, 0);
        notes.push(`${params.name}: ${params.temperatureC}C, ${params.holdTimeHours}hr hold`);
        notesTR.push(`${params.nameTR}: ${params.temperatureC}C, ${params.holdTimeHours} saat tutma`);

        return { costPerPart: Math.round(costPerPart * 100) / 100, breakdown, notes, notesTR };
    }

    // ================================================================
    // SURFACE TREATMENT
    // ================================================================

    calculateSurfaceTreatment(input: SurfaceTreatmentCostInput): SurfaceTreatmentCostResult {
        const { surfaceAreaCm2, material, treatmentType, batchSize } = input;
        const notes: string[] = [];
        const notesTR: string[] = [];
        const breakdown: CostLineItem[] = [];

        const ratePerCm2 = this.getSurfaceRatePerCm2(treatmentType);
        const treatmentCost = surfaceAreaCm2 * ratePerCm2;

        breakdown.push({
            category: 'post_processing',
            description: `${this.getSurfaceTreatmentName(treatmentType)} (${surfaceAreaCm2.toFixed(0)} cm2 at $${ratePerCm2.toFixed(4)}/cm2)`,
            descriptionTR: `${this.getSurfaceTreatmentNameTR(treatmentType)} (${surfaceAreaCm2.toFixed(0)} cm2, $${ratePerCm2.toFixed(4)}/cm2)`,
            quantity: surfaceAreaCm2,
            unitCost: ratePerCm2,
            totalCost: treatmentCost,
            unit: 'cm2'
        });

        // Yuzey hazirlama (temizleme, astar)
        const prepCost = surfaceAreaCm2 * 0.002;
        breakdown.push({
            category: 'labor',
            description: 'Surface preparation (cleaning, masking)',
            descriptionTR: 'Yuzey hazirlama (temizlik, maskeleme)',
            quantity: 1,
            unitCost: prepCost,
            totalCost: prepCost,
            unit: 'adet'
        });

        // Kurutma/kurleme
        if (treatmentType === 'painting' || treatmentType === 'powder_coating') {
            const curingCost = 0.5;
            breakdown.push({
                category: 'machine',
                description: 'Oven curing',
                descriptionTR: 'Firin kurleme',
                quantity: 1,
                unitCost: curingCost,
                totalCost: curingCost,
                unit: 'adet'
            });
        }

        // Setup (banyo/boya hatti hazirlik)
        const setupCost = this.getSurfaceSetupCost(treatmentType) / batchSize;
        if (setupCost > 0.01) {
            breakdown.push({
                category: 'setup',
                description: `Line setup / ${batchSize} parts`,
                descriptionTR: `Hat hazirlik / ${batchSize} parca`,
                quantity: 1,
                unitCost: setupCost,
                totalCost: setupCost,
                unit: 'adet'
            });
        }

        const costPerPart = breakdown.reduce((sum, item) => sum + item.totalCost, 0);
        notes.push(`${this.getSurfaceTreatmentName(treatmentType)}: ${surfaceAreaCm2.toFixed(0)} cm2`);
        notesTR.push(`${this.getSurfaceTreatmentNameTR(treatmentType)}: ${surfaceAreaCm2.toFixed(0)} cm2`);

        return { costPerPart: Math.round(costPerPart * 100) / 100, breakdown, notes, notesTR };
    }

    // ================================================================
    // FORGING HELPERS
    // ================================================================

    private estimateForgingDieCost(geom: any, material: MaterialProperties): number {
        const bbVol = geom.boundingBox.x * geom.boundingBox.y * geom.boundingBox.z;
        const sizeFactor = Math.pow(bbVol / 1e6, 0.5);
        const complexityFactor = 1 + (geom.complexityScore / 100) * 2;
        return 5000 * sizeFactor * complexityFactor;
    }

    private estimatePressTonnage(geom: any, material: MaterialProperties): number {
        const projectedAreaCm2 = (geom.boundingBox.x * geom.boundingBox.y) / 100;
        const flowStress = material.tensileStrength * 1.5; // Approximate at forging temp
        const tonnage = (projectedAreaCm2 * flowStress * 0.01) / 9.81; // Convert to metric tons
        const stdSizes = [100, 250, 500, 1000, 1600, 2500, 4000, 6300, 10000, 16000];
        return stdSizes.find(s => s >= tonnage) || tonnage;
    }

    private getPressRate(tonnage: number): number {
        if (tonnage <= 500) return 45;
        if (tonnage <= 1000) return 60;
        if (tonnage <= 2500) return 80;
        if (tonnage <= 5000) return 110;
        return 150;
    }

    // ================================================================
    // WELDING HELPERS
    // ================================================================

    private getWireConsumption(weldType: string, thickness: number): number {
        // kg wire per meter of weld
        const base: Record<string, number> = {
            fillet: 0.015,
            butt: 0.020,
            lap: 0.012,
            spot: 0.001,
        };
        return (base[weldType] || 0.015) * (1 + (thickness - 2) * 0.1);
    }

    private getWeldSpeed(process: WeldingProcessType, thickness: number): number {
        // mm/min
        const base: Record<WeldingProcessType, number> = {
            welding_mig: 300,
            welding_tig: 100,
            welding_spot: 0, // Not applicable
            welding_laser: 1000,
        };
        const speed = base[process] || 200;
        // Thicker material = slower
        return speed * Math.max(0.3, 1 - (thickness - 2) * 0.05);
    }

    // ================================================================
    // HEAT TREATMENT HELPERS
    // ================================================================

    private getHeatTreatmentParams(type: HeatTreatmentType, material: MaterialProperties): {
        name: string; nameTR: string; temperatureC: number; holdTimeHours: number; energyCostPerKg: number;
    } {
        const params: Record<HeatTreatmentType, any> = {
            annealing: { name: 'Annealing', nameTR: 'Tavlama', temperatureC: 850, holdTimeHours: 2, energyCostPerKg: 0.6 },
            normalizing: { name: 'Normalizing', nameTR: 'Normalizasyon', temperatureC: 900, holdTimeHours: 1.5, energyCostPerKg: 0.5 },
            quench_temper: { name: 'Quench & Temper', nameTR: 'Su verme & Menevislenme', temperatureC: 850, holdTimeHours: 2, energyCostPerKg: 1.0 },
            case_hardening: { name: 'Case Hardening', nameTR: 'Sementasyon', temperatureC: 920, holdTimeHours: 6, energyCostPerKg: 2.5 },
            stress_relief: { name: 'Stress Relief', nameTR: 'Gerilim Giderme', temperatureC: 600, holdTimeHours: 1, energyCostPerKg: 0.3 },
            aging: { name: 'Aging', nameTR: 'Yaslandirma', temperatureC: 480, holdTimeHours: 4, energyCostPerKg: 0.8 },
        };
        return params[type] || params.annealing;
    }

    // ================================================================
    // SURFACE TREATMENT HELPERS
    // ================================================================

    private getSurfaceRatePerCm2(type: SurfaceTreatmentType): number {
        const rates: Record<SurfaceTreatmentType, number> = {
            anodizing: 0.008,
            plating: 0.015,
            painting: 0.005,
            powder_coating: 0.007,
        };
        return rates[type] || 0.005;
    }

    private getSurfaceSetupCost(type: SurfaceTreatmentType): number {
        const costs: Record<SurfaceTreatmentType, number> = {
            anodizing: 50,
            plating: 80,
            painting: 30,
            powder_coating: 40,
        };
        return costs[type] || 30;
    }

    private getSurfaceTreatmentName(type: SurfaceTreatmentType): string {
        const names: Record<SurfaceTreatmentType, string> = { anodizing: 'Anodizing', plating: 'Plating (Nickel/Chrome)', painting: 'Wet Painting', powder_coating: 'Powder Coating' };
        return names[type] || type;
    }

    private getSurfaceTreatmentNameTR(type: SurfaceTreatmentType): string {
        const names: Record<SurfaceTreatmentType, string> = { anodizing: 'Anodizasyon', plating: 'Kaplama (Nikel/Krom)', painting: 'Yas Boya', powder_coating: 'Toz Boya' };
        return names[type] || type;
    }
}
